import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, Phone, MessageSquare, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RiskBanner, RiskFooter } from "@/components/RiskDisclaimer";
import { contactApi } from "@/lib/api";
import { toast } from "sonner";

const initialForm = { full_name: "", phone: "", email: "", message: "" };

function isValidEmail(e) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

export default function ContactPage() {
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setError(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const name = form.full_name.trim();
    const email = form.email.trim();
    const message = form.message.trim();
    const phone = form.phone.trim();
    if (!name) { setError("Please enter your full name."); return; }
    if (!phone || phone.replace(/\D/g, "").length < 7) { setError("Please enter a valid phone number."); return; }
    if (!isValidEmail(email)) { setError("Please enter a valid email address."); return; }
    if (message.length < 5) { setError("Please write a slightly longer message so we can help you."); return; }

    setBusy(true);
    try {
      const r = await contactApi.submit({
        full_name: name,
        phone,
        email,
        message,
      });
      setDone(true);
      toast.success(r?.message || "Message sent!");
    } catch (err) {
      console.error("[Contact] submit failed", err);
      setError(err?.response?.data?.detail || "Something went wrong. Please try again or text us at 901-361-9173.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C12] text-slate-100 flex flex-col">
      <RiskBanner />
      <header className="max-w-5xl w-full mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/" data-testid="contact-back-home" className="flex items-center gap-3 group">
          <img
            src="/brand/tradingox-logo.png"
            alt="TradingoX"
            className="w-10 h-10 rounded-full ring-1 ring-white/10 shadow-[0_0_20px_rgba(16,185,129,0.25)]"
          />
          <div className="font-display text-lg font-bold tracking-tight">TradingoX</div>
        </Link>
        <Link
          to="/"
          data-testid="contact-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-100 transition"
        >
          <ArrowLeft className="w-4 h-4" /> Back home
        </Link>
      </header>

      <main className="max-w-3xl w-full mx-auto px-6 pt-8 pb-16 flex-1">
        <div className="mb-10 text-center">
          <div className="text-[11px] font-mono uppercase tracking-[0.35em] text-emerald-300 mb-2">
            get in touch
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            Contact <span className="text-emerald-400">Us</span>
          </h1>
          <p className="mt-3 text-slate-400 text-base max-w-xl mx-auto">
            Two easy ways to reach us — send a message or shoot a text.
          </p>
        </div>

        <div className="grid md:grid-cols-[1fr_320px] gap-8">
          {/* Option 1: Contact form */}
          <section
            data-testid="contact-form-section"
            className="rounded-2xl border border-white/10 bg-[#0F111A]/70 backdrop-blur-sm p-6 sm:p-8"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="grid place-items-center w-9 h-9 rounded-full bg-emerald-500/15 text-emerald-300">
                <MessageSquare className="w-4 h-4" />
              </span>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">option 1</div>
                <div className="text-lg font-semibold text-slate-100">Send us a message</div>
              </div>
            </div>
            <p className="text-xs text-slate-400 mb-6 pl-11">
              We reply within 1 business day.
            </p>

            {done ? (
              <div
                data-testid="contact-success"
                className="text-center py-8 space-y-3"
              >
                <div className="mx-auto w-14 h-14 rounded-full bg-emerald-500/15 grid place-items-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-300" />
                </div>
                <div className="font-display text-xl font-bold">Message sent!</div>
                <p className="text-sm text-slate-400 max-w-md mx-auto">
                  Thanks, {form.full_name.trim().split(" ")[0]}. We've received your inquiry and will get back to
                  you at <span className="text-emerald-300">{form.email}</span> shortly.
                </p>
                <Button
                  data-testid="contact-send-another"
                  onClick={() => { setForm(initialForm); setDone(false); }}
                  variant="outline"
                  className="mt-2 border-white/10 hover:bg-white/5"
                >
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate className="space-y-4">
                <div>
                  <label htmlFor="contact-full-name" className="text-xs font-medium text-slate-300 mb-1.5 block">
                    Full name <span className="text-emerald-300">*</span>
                  </label>
                  <Input
                    id="contact-full-name"
                    data-testid="contact-input-name"
                    value={form.full_name}
                    onChange={update("full_name")}
                    placeholder="Charles Cawv Thao"
                    maxLength={100}
                    autoComplete="name"
                    className="bg-[#0D0E14] border-white/10 h-11"
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="text-xs font-medium text-slate-300 mb-1.5 block">
                    Phone number <span className="text-emerald-400">*</span>
                  </label>
                  <Input
                    id="contact-phone"
                    data-testid="contact-input-phone"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="(555) 555-5555"
                    maxLength={40}
                    autoComplete="tel"
                    inputMode="tel"
                    required
                    className="bg-[#0D0E14] border-white/10 h-11"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="text-xs font-medium text-slate-300 mb-1.5 block">
                    Email address <span className="text-emerald-300">*</span>
                  </label>
                  <Input
                    id="contact-email"
                    data-testid="contact-input-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="you@example.com"
                    maxLength={200}
                    autoComplete="email"
                    className="bg-[#0D0E14] border-white/10 h-11"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="text-xs font-medium text-slate-300 mb-1.5 block">
                    Inquiry message <span className="text-emerald-300">*</span>
                  </label>
                  <Textarea
                    id="contact-message"
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update("message")}
                    rows={5}
                    maxLength={4000}
                    placeholder="Tell us what you'd like to know…"
                    className="bg-[#0D0E14] border-white/10 resize-y min-h-[120px]"
                  />
                  <div className="mt-1 text-right text-[10px] text-slate-500">
                    {form.message.length} / 4000
                  </div>
                </div>

                {error && (
                  <div data-testid="contact-error" className="text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                    {error}
                  </div>
                )}

                <Button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={busy}
                  className="w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold gap-2"
                >
                  <Send className="w-4 h-4" />
                  {busy ? "Sending…" : "Send message"}
                </Button>
              </form>
            )}
          </section>

          {/* Option 2: Text us */}
          <section
            data-testid="contact-text-section"
            className="rounded-2xl border border-emerald-400/20 bg-gradient-to-br from-emerald-500/10 via-transparent to-transparent p-6 sm:p-8 h-fit"
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="grid place-items-center w-9 h-9 rounded-full bg-emerald-500/15 text-emerald-300">
                <Phone className="w-4 h-4" />
              </span>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-emerald-300">option 2</div>
                <div className="text-lg font-semibold text-slate-100">Text us</div>
              </div>
            </div>
            <p className="text-xs text-slate-400 mb-4 pl-11">
              Prefer a quick text? Ping us directly.
            </p>
            <a
              data-testid="contact-sms-link"
              href="sms:+19013619173"
              className="block rounded-xl border border-white/10 bg-[#0D0E14] hover:border-emerald-400/40 transition p-4 text-center"
            >
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300">tap to text</div>
              <div className="mt-1 font-display text-2xl font-bold tracking-tight text-slate-100">
                901-361-9173
              </div>
            </a>
            <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
              Standard message rates apply. Available 9am–7pm CT weekdays.
            </p>
          </section>
        </div>
      </main>

      <RiskFooter />
    </div>
  );
}
